import { z } from 'zod'

/**
 * 「代表 + 自社メンバー」を持つマスタ (BG マスタ / 発電所マスタ) で共通の
 * メンバー検証ルール。
 *
 * 代表 (representative) はフラグでは表現せず、 代表事業者の businessMasterId が
 * members に含まれることで表現する (= bg_master.rep_business_master_id ∈ members、
 * backend の validateBgMasterMembers / validatePlantMasterMembers と同方式)。
 *
 * 検証内容:
 *  - isOwn=true は最大 1 件 (= 自社は 0 件 or 1 件)
 *  - 代表事業者が members に必ず含まれる
 *  - members の businessMasterId は重複不可 (= backend が重複を弾く前に親切な
 *    メッセージを出す。 代表セレクタが既存メンバーと同じ事業者を members[0] に
 *    同期した場合などに発生しうる)
 */
export type OwnerMemberInput = {
  businessMasterId: string
  isOwn: boolean
}

type AddOwnerMemberIssuesParams = {
  members: OwnerMemberInput[]
  /** 代表事業者の businessMasterId (= members に含まれているべき値) */
  repBusinessMasterId: string
  /** 業務上の呼称 (BG マスタ: 「所属事業者」 / 発電所マスタ: 「合同所有者」) */
  memberNoun: string
  /** 代表事業者が members に無い時のエラー表示先 path */
  repPath: 'repBusinessMasterId' | 'businessMasterId'
}

/**
 * 上記ルールに違反する issue を superRefine の ctx に追加する。
 * 各マスタの superRefine から呼び、 マスタ固有の cross-field 検証 (コード整合等)
 * と併用する。
 */
export function addOwnerMemberIssues(
  ctx: z.RefinementCtx,
  {
    members,
    repBusinessMasterId,
    memberNoun,
    repPath,
  }: AddOwnerMemberIssuesParams,
): void {
  // isOwn=true は最大 1 件 (AC-OMB-009 / plant.md AC-OMP-008 共通文言)
  const ownCount = members.filter((m) => m.isOwn).length
  if (ownCount > 1) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message:
        '自社に指定できる事業者は 1件のみです。既存の自社指定を解除してから選択してください',
      path: ['members'],
    })
  }

  // businessMasterId の重複不可 (AC-BG10)
  const seen = new Set<string>()
  const hasDuplicate = members.some((m) => {
    if (seen.has(m.businessMasterId)) {
      return true
    }
    seen.add(m.businessMasterId)
    return false
  })
  if (hasDuplicate) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: `${memberNoun}に同じ事業者が重複しています。重複している行を削除してください`,
      path: ['members'],
    })
  }

  // 代表事業者が members に含まれる (AC-BG11)
  const containsRep = members.some(
    (m) => m.businessMasterId === repBusinessMasterId,
  )
  if (!containsRep) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: `代表事業者を${memberNoun}の一覧に追加してください`,
      path: [repPath],
    })
  }
}
